<template>
	<div>
		<main class="container-xl py-3">

			<!-- Owner-controls notice -->
			<div class="rounded mb-3 px-3 py-2" style="background:#fef3c7;border:1px solid #fcd34d;font-size:12px;color:#78350f;">
				<span class="fw-semibold">Role-gating coming soon</span> — treat uploads and deletes as owner-only for now.
			</div>

			<!-- Header + actions -->
			<div class="d-flex align-items-baseline justify-content-between mb-3 gap-2 flex-wrap">
				<h2 class="h5 fw-semibold mb-0 text-dark">Past proposals</h2>
				<div class="d-flex gap-2">
					<button type="button" @click="showBulkParse = !showBulkParse"
						class="btn btn-sm" style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:12px;padding:6px 12px;">
						{{ showBulkParse ? 'Hide bulk parse' : 'Bulk parse (PDFs + JPEGs)' }}
					</button>
					<label class="btn btn-sm mb-0" style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:12px;padding:6px 12px;cursor:pointer;">
						Upload CSV
						<input type="file" accept=".csv,.txt" class="d-none" ref="csvFile" @change="importCsv" />
					</label>
					<button type="button" @click="openAddModal"
						class="btn btn-sm btn-primary fw-medium" style="font-size:12px;padding:6px 12px;">
						+ Add proposal
					</button>
				</div>
			</div>

			<!-- Bulk parse panel (lazy — instantiates only when shown) -->
			<div v-if="showBulkParse" class="mb-3">
				<CwpeBulkParsePanel @imported="onBulkImported" />
			</div>

			<!-- Stats strip -->
			<div v-if="stats" class="row row-cols-2 row-cols-md-5 g-2 mb-3">
				<div class="col">
					<div class="rounded p-3 h-100" style="border:1px solid #e2e8f0;background:white;">
						<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">Total proposals</div>
						<div class="fw-semibold" style="font-size:22px;color:#0f172a;">{{ Number(stats.total || 0).toLocaleString('en-US') }}</div>
					</div>
				</div>
				<div class="col">
					<div class="rounded p-3 h-100" style="border:1px solid #a7f3d0;background:rgba(236,253,245,0.5);">
						<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#047857;">Won</div>
						<div class="fw-semibold" style="font-size:22px;color:#064e3b;">{{ Number(stats.won || 0).toLocaleString('en-US') }}</div>
					</div>
				</div>
				<div class="col">
					<div class="rounded p-3 h-100" style="border:1px solid #fecdd3;background:rgba(255,241,242,0.5);">
						<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#be123c;">Lost</div>
						<div class="fw-semibold" style="font-size:22px;color:#881337;">{{ Number(stats.lost || 0).toLocaleString('en-US') }}</div>
					</div>
				</div>
				<div class="col">
					<div class="rounded p-3 h-100" style="border:1px solid #e2e8f0;background:white;">
						<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">Win rate</div>
						<div class="fw-semibold" style="font-size:22px;color:#0f172a;">{{ stats.winRate != null ? (stats.winRate * 100).toFixed(0) + '%' : '—' }}</div>
						<div style="font-size:10px;color:#64748b;">excl. {{ stats.pending || 0 }} pending</div>
					</div>
				</div>
				<div class="col">
					<div class="rounded p-3 h-100" style="border:1px solid #e2e8f0;background:white;">
						<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">Median annual (won)</div>
						<div class="fw-semibold" style="font-size:22px;color:#0f172a;">{{ stats.medianAnnualCents ? fmtCents(stats.medianAnnualCents) : '—' }}</div>
						<div v-if="stats.avgContractYears" style="font-size:10px;color:#64748b;">avg {{ stats.avgContractYears }} yr term</div>
					</div>
				</div>
			</div>

			<!-- Bulk maintenance — Enrich unmatched + Re-parse missing line items -->
			<div class="rounded bg-white px-3 py-2 mb-3 d-flex align-items-baseline justify-content-between gap-3 flex-wrap"
				style="border:1px solid #e2e8f0;font-size:12px;">
				<div style="color:#475569;">
					<span v-if="unmatchedCount > 0 || enrichSummary" class="me-3">
						<span v-if="unmatchedCount > 0" style="color:#92400e;">
							<strong>{{ unmatchedCount }}</strong> proposal{{ unmatchedCount === 1 ? '' : 's' }} not linked to a municipality.
						</span>
						<span v-if="enrichSummary" class="ms-2" style="color:#64748b;">{{ enrichSummary }}</span>
					</span>
					<span>Parse line items from stored PDFs to power the comparison panel.</span>
					<span v-if="reparseSummary" class="ms-2" style="color:#64748b;">{{ reparseSummary }}</span>
				</div>
				<div class="d-flex gap-2 flex-shrink-0">
					<button type="button" @click="enrichUnmatched" :disabled="enriching"
						class="btn btn-sm btn-primary fw-medium" style="font-size:12px;padding:4px 10px;">
						<span v-if="enriching" class="spinner-border spinner-border-sm me-1"></span>
						{{ enriching ? 'Enriching…' : 'Enrich unmatched' }}
					</button>
					<button type="button" @click="reparseMissing" :disabled="reparsing"
						class="btn btn-sm" style="border:1px solid #1d4ed8;background:white;color:#1d4ed8;font-size:12px;padding:4px 10px;">
						<span v-if="reparsing" class="spinner-border spinner-border-sm me-1"></span>
						{{ reparsing ? 'Parsing…' : 'Parse line items (missing)' }}
					</button>
				</div>
			</div>

			<!-- Filters -->
			<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
				<div class="row g-2 align-items-end">
					<div class="col-md-4">
						<label class="form-label mb-1" style="font-size:11px;font-weight:500;color:#475569;">Search</label>
						<input type="text" v-model="filters.search" @input="onFilterChange"
							placeholder="Municipality name…" class="form-control form-control-sm" />
					</div>
					<div class="col-md-2">
						<label class="form-label mb-1" style="font-size:11px;font-weight:500;color:#475569;">State</label>
						<select class="form-select form-select-sm" v-model="filters.state" @change="onFilterChange">
							<option value="">any</option>
							<option v-for="s in US_STATES" :key="s" :value="s">{{ s }}</option>
						</select>
					</div>
					<div class="col-md-2">
						<label class="form-label mb-1" style="font-size:11px;font-weight:500;color:#475569;">Outcome</label>
						<select class="form-select form-select-sm" v-model="filters.outcome" @change="onFilterChange">
							<option value="">any</option>
							<option value="won">won</option>
							<option value="lost">lost</option>
							<option value="pending">pending</option>
						</select>
					</div>
					<div class="col-md-2">
						<label class="form-label mb-1" style="font-size:11px;font-weight:500;color:#475569;">New / Upgrade</label>
						<select class="form-select form-select-sm" v-model="filters.customer_status" @change="onFilterChange">
							<option value="">any</option>
							<option value="new">new</option>
							<option value="upgrade">upgrade</option>
						</select>
					</div>
					<div class="col-md-2 text-end">
						<button type="button" @click="resetFilters"
							class="btn btn-sm" style="border:1px solid #cbd5e1;background:white;color:#475569;font-size:12px;padding:6px 12px;">
							Reset
						</button>
					</div>
				</div>
			</div>

			<!-- Showing X of Y -->
			<div class="d-flex align-items-baseline justify-content-between mb-2" style="font-size:12px;color:#64748b;">
				<div>
					Showing <span class="fw-semibold">{{ proposals.length }}</span> of <span class="fw-semibold">{{ pagination ? pagination.total : 0 }}</span>
					proposal{{ ((pagination && pagination.total) || 0) === 1 ? '' : 's' }}
				</div>
				<div v-if="pagination && pagination.total > pagination.per_page" class="d-flex gap-1">
					<button class="btn btn-sm btn-outline-secondary" :disabled="currentPage <= 1" @click="prevPage">Prev</button>
					<button class="btn btn-sm btn-outline-secondary" :disabled="!pagination || currentPage >= pagination.last_page" @click="nextPage">Next</button>
				</div>
			</div>

			<div v-if="loading" class="text-center py-5">
				<div class="spinner-border" role="status"></div>
			</div>

			<div v-else-if="!proposals.length" class="rounded bg-white text-center py-5" style="border:1px solid #e2e8f0;font-size:14px;color:#64748b;">
				No proposals match these filters. Upload a CSV or click "+ Add proposal" to get started.
			</div>

			<div v-else class="rounded bg-white overflow-hidden" style="border:1px solid #e2e8f0;">
				<table class="table table-sm table-hover mb-0" style="font-size:12px;">
					<thead style="background:#f8fafc;color:#475569;">
						<tr>
							<th class="text-start fw-medium px-3 py-2" style="cursor:pointer;user-select:none;" @click="toggleSort('municipality_name_raw')">
								Municipality {{ sortArrow('municipality_name_raw') }}
							</th>
							<th class="text-end fw-medium px-2 py-2">Pop</th>
							<th class="text-end fw-medium px-2 py-2" style="cursor:pointer;user-select:none;" @click="toggleSort('setup_cents')">
								Setup {{ sortArrow('setup_cents') }}
							</th>
							<th class="text-end fw-medium px-2 py-2" style="cursor:pointer;user-select:none;background:rgba(236,253,245,0.4);" @click="toggleSort('annual_cents')">
								Annual {{ sortArrow('annual_cents') }}
							</th>
							<th class="text-end fw-medium px-2 py-2">First-yr</th>
							<th class="text-end fw-medium px-2 py-2" style="background:rgba(219,234,254,0.4);"
								title="Engine's recommended first-year total — recomputed live against current owner-controls settings">Engine 1st-yr</th>
							<th class="text-end fw-medium px-2 py-2" style="background:rgba(219,234,254,0.4);"
								title="Quoted contract total − Engine contract total. Blue = premium, red = discount.">Δ vs engine</th>
							<th class="text-center fw-medium px-2 py-2">Yrs</th>
							<th class="text-center fw-medium px-2 py-2">New / Upgrade</th>
							<th class="text-center fw-medium px-2 py-2">Outcome</th>
							<th class="text-start fw-medium px-2 py-2" style="cursor:pointer;user-select:none;" @click="toggleSort('signed_date')">
								Signed {{ sortArrow('signed_date') }}
							</th>
							<th class="text-center fw-medium px-2 py-2">Files</th>
							<th style="width:32px;"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="p in proposals" :key="p.id" style="border-top:1px solid #e2e8f0;cursor:pointer;"
							@click="$router.push(`/cwpe/proposals/${p.id}`)">
							<td class="px-3 py-2">
								<div class="fw-medium text-dark" style="font-size:13px;">
									{{ p.municipality_name_raw }}<span class="fw-normal" style="color:#94a3b8;">, {{ p.state }}</span>
								</div>
								<div v-if="p.municipality && p.municipality.county_name" style="font-size:10px;color:#64748b;">
									{{ p.municipality.county_name }}
								</div>
								<div v-else-if="!p.municipality_id" style="font-size:10px;color:#92400e;">⚠ unmatched</div>
							</td>
							<td class="px-2 py-2 text-end font-monospace">
								{{ p.population != null ? Number(p.population).toLocaleString('en-US') : '—' }}
							</td>
							<td class="px-2 py-2 text-end font-monospace" style="color:#475569;">{{ fmtCents(p.setup_cents) }}</td>
							<td class="px-2 py-2 text-end font-monospace fw-semibold" style="background:rgba(236,253,245,0.4);color:#065f46;">{{ fmtCents(p.annual_cents) }}</td>
							<td class="px-2 py-2 text-end font-monospace fw-semibold" style="color:#0f172a;">{{ fmtCents((p.setup_cents || 0) + (p.annual_cents || 0)) }}</td>
							<td class="px-2 py-2 text-end font-monospace" style="background:rgba(219,234,254,0.2);">
								<template v-if="p.engine">
									<div class="fw-semibold" style="color:#1e3a8a;">{{ fmtCents(p.engine.firstYearCents) }}</div>
									<div style="font-size:9px;color:#64748b;line-height:1;">{{ shortBundleName(p.engine.bundleName) }}<span v-if="p.engine.bundleIsPinned" title="Pinned to this bundle"> 📌</span></div>
								</template>
								<span v-else style="color:#cbd5e1;font-size:10px;">—</span>
							</td>
							<td class="px-2 py-2 text-end font-monospace" style="background:rgba(219,234,254,0.2);"
								:style="{ color: p.engine ? deltaColor(p.engine.deltaContractTotalCents) : '' }">
								<template v-if="p.engine">
									<div class="fw-semibold">{{ fmtSignedCents(p.engine.deltaContractTotalCents) }}</div>
									<div v-if="p.engine.deltaPctOfEngineContract != null" style="font-size:9px;line-height:1;">{{ (p.engine.deltaPctOfEngineContract * 100).toFixed(0) }}% over {{ p.contract_years }}yr</div>
								</template>
								<span v-else style="color:#cbd5e1;font-size:10px;">—</span>
							</td>
							<td class="px-2 py-2 text-center font-monospace">{{ p.contract_years || '—' }}</td>
							<td class="px-2 py-2 text-center">
								<span class="rounded fw-medium px-2 py-0" style="font-size:10px;"
									:style="p.customer_status === 'upgrade'
										? { background:'#e0f2fe', color:'#075985' }
										: { background:'#f1f5f9', color:'#334155' }">
									{{ p.customer_status }}
								</span>
							</td>
							<td class="px-2 py-2 text-center">
								<span class="rounded-pill fw-medium px-2 py-0" style="font-size:10px;border:1px solid;"
									:style="outcomeStyle(p.outcome)">
									{{ p.outcome }}
								</span>
							</td>
							<td class="px-2 py-2 font-monospace" style="color:#475569;">{{ p.signed_date ? fmtDate(p.signed_date) : '—' }}</td>
							<td class="px-2 py-2 text-center" @click.stop>
								<span v-if="(p.files && p.files.length) > 0" class="rounded fw-medium px-2 py-0" style="font-size:10px;background:#f1f5f9;color:#334155;">
									📎 {{ p.files.length }}
								</span>
								<span v-else style="color:#cbd5e1;font-size:10px;">—</span>
							</td>
							<td class="px-2 py-2 text-center" @click.stop>
								<button type="button" @click="deleteProposal(p)"
									class="btn btn-link p-0" style="color:#cbd5e1;font-size:18px;line-height:1;text-decoration:none;"
									title="Delete">×</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</main>

		<!-- Add modal — visual port of Clerkware/Nuxt ProposalForm.vue -->
		<div v-if="showAddModal"
			class="position-fixed top-0 start-0 w-100 h-100"
			style="z-index:1050;background:rgba(2,6,23,0.6);backdrop-filter:blur(4px);padding:24px;overflow-y:auto;"
			@click.self="closeAddModal">
			<form @submit.prevent="addProposal"
				class="bg-white rounded shadow-lg w-100"
				style="max-width:640px;padding:20px;margin:0 auto;">

				<div class="d-flex align-items-baseline justify-content-between mb-3">
					<h2 class="h5 fw-semibold mb-0" style="color:#0f172a;">Add proposal</h2>
					<button type="button" @click="closeAddModal"
						class="btn btn-link p-0" style="color:#94a3b8;font-size:24px;line-height:1;text-decoration:none;">×</button>
				</div>

				<div class="row g-3" style="font-size:12px;">
					<!-- City / municipality -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">City / municipality</label>
						<input v-model="newProposal.municipality_name_raw" required type="text" placeholder="Forestville"
							class="form-control" style="font-size:14px;" />
					</div>
					<!-- State -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">State</label>
						<select v-model="newProposal.state" class="form-select" style="font-size:14px;">
							<option v-for="s in US_STATES" :key="s" :value="s">{{ s }}</option>
						</select>
					</div>
					<!-- Population -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">
							Population <span class="fw-normal" style="color:#94a3b8;">(optional)</span>
						</label>
						<input v-model.number="newProposal.population" type="number" min="0" placeholder="457"
							class="form-control font-monospace" style="font-size:14px;" />
					</div>
					<!-- Customer -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">Customer</label>
						<select v-model="newProposal.customer_status" class="form-select" style="font-size:14px;">
							<option value="new">New</option>
							<option value="upgrade">Upgrade (current customer)</option>
						</select>
					</div>
					<!-- Setup -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">Setup cost ($)</label>
						<input v-model.number="newProposal.setup_dollars" required type="number" min="0" step="1" placeholder="537"
							class="form-control font-monospace" style="font-size:14px;" />
					</div>
					<!-- Annual -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">Annual cost ($)</label>
						<input v-model.number="newProposal.annual_dollars" required type="number" min="0" step="1" placeholder="811"
							class="form-control font-monospace" style="font-size:14px;" />
					</div>
					<!-- Contract years -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">Contract years</label>
						<select v-model.number="newProposal.contract_years" class="form-select" style="font-size:14px;">
							<option v-for="y in [1, 2, 3, 4, 5]" :key="y" :value="y">{{ y }}</option>
						</select>
					</div>
					<!-- Signed date -->
					<div class="col-12 col-md-6">
						<label class="d-block fw-medium mb-1" style="color:#334155;">Signed date</label>
						<input v-model="newProposal.signed_date" required type="date"
							class="form-control" style="font-size:14px;" />
					</div>
					<!-- Outcome — three-button group, color-coded -->
					<div class="col-12">
						<label class="d-block fw-medium mb-1" style="color:#334155;">Outcome</label>
						<div class="d-flex gap-2">
							<label v-for="o in ['won', 'lost', 'pending']" :key="o"
								class="flex-fill text-center rounded text-capitalize"
								:style="outcomeButtonStyle(o)"
								style="cursor:pointer;padding:8px 12px;font-size:13px;">
								<input v-model="newProposal.outcome" type="radio" :value="o" class="visually-hidden" />
								{{ o }}
							</label>
						</div>
					</div>
					<!-- Notes -->
					<div class="col-12">
						<label class="d-block fw-medium mb-1" style="color:#334155;">
							Notes <span class="fw-normal" style="color:#94a3b8;">(optional)</span>
						</label>
						<textarea v-model="newProposal.notes" rows="2"
							placeholder="Upgraded from legacy site; 3-year term…"
							class="form-control" style="font-size:14px;"></textarea>
					</div>
				</div>

				<p v-if="addError" class="mb-0 mt-3" style="font-size:12px;color:#9f1239;">{{ addError }}</p>

				<div class="d-flex justify-content-end gap-2 pt-3 mt-3" style="border-top:1px solid #e2e8f0;">
					<button type="button" @click="closeAddModal"
						class="btn" style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:13px;padding:6px 14px;">Cancel</button>
					<button type="submit" :disabled="addSaving"
						class="btn btn-primary fw-medium" style="font-size:13px;padding:6px 16px;">
						<span v-if="addSaving" class="spinner-border spinner-border-sm me-1"></span>
						{{ addSaving ? 'Saving…' : 'Save proposal' }}
					</button>
				</div>
			</form>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index.js'
import CwpeBulkParsePanel from './CwpeBulkParsePanel.vue'

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC']

export default {
	name: 'CwpeProposals',
	components: { CwpeBulkParsePanel },
	data() {
		return {
			US_STATES,
			proposals: [],
			pagination: null,
			stats: null,
			loading: false,
			currentPage: 1,
			perPage: 25,
			filters: { state: '', outcome: '', customer_status: '', search: '' },
			sortKey: 'signed_date',
			sortDir: 'desc',
			debounceTimer: null,
			showAddModal: false,
			showBulkParse: false,
			addSaving: false,
			addError: null,
			newProposal: this.blankProposal(),
			enriching: false,
			enrichSummary: null,
			reparsing: false,
			reparseSummary: null,
		}
	},
	computed: {
		unmatchedCount() {
			return (this.stats && this.stats.unmatchedCount) || 0
		},
	},
	created() {
		this.load()
		this.loadStats()
	},
	watch: {
		showAddModal(open) {
			document.body.style.overflow = open ? 'hidden' : ''
		},
	},
	beforeDestroy() {
		document.body.style.overflow = ''
	},
	methods: {
		blankProposal() {
			return {
				municipality_name_raw: '',
				state: 'WI',
				population: null,
				setup_dollars: null,
				annual_dollars: null,
				contract_years: 1,
				customer_status: 'new',
				outcome: 'won',
				signed_date: new Date().toISOString().slice(0, 10),
				notes: '',
			}
		},
		load() {
			this.loading = true
			const params = {
				page: this.currentPage,
				per_page: this.perPage,
				order_by: this.sortKey,
				direction: this.sortDir,
			}
			if (this.filters.state) params.state = this.filters.state
			if (this.filters.outcome) params.outcome = this.filters.outcome
			if (this.filters.customer_status) params.customer_status = this.filters.customer_status
			if (this.filters.search) params.search = this.filters.search
			restApi.get('/cwpe/proposals', { params }).then(({ data }) => {
				this.proposals = data.data || []
				this.pagination = data
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Failed to load', text: err?.response?.data?.error || '' })
			}).finally(() => { this.loading = false })
		},
		loadStats() {
			restApi.get('/cwpe/proposals/stats').then(({ data }) => {
				this.stats = data
			}).catch(() => {})
		},
		onFilterChange() {
			clearTimeout(this.debounceTimer)
			this.debounceTimer = setTimeout(() => { this.currentPage = 1; this.load() }, 250)
		},
		resetFilters() {
			this.filters = { state: '', outcome: '', customer_status: '', search: '' }
			this.currentPage = 1
			this.load()
		},
		toggleSort(col) {
			// Map UI sort key to backend column name
			const supported = ['municipality_name_raw', 'setup_cents', 'annual_cents', 'signed_date']
			if (!supported.includes(col)) return
			if (this.sortKey === col) {
				this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
			} else {
				this.sortKey = col
				this.sortDir = col === 'municipality_name_raw' ? 'asc' : 'desc'
			}
			this.currentPage = 1
			this.load()
		},
		sortArrow(col) {
			if (this.sortKey !== col) return ''
			return this.sortDir === 'asc' ? '↑' : '↓'
		},
		prevPage() { if (this.currentPage > 1) { this.currentPage--; this.load() } },
		nextPage() {
			if (this.pagination && this.currentPage < this.pagination.last_page) { this.currentPage++; this.load() }
		},
		openAddModal() {
			this.newProposal = this.blankProposal()
			this.addError = null
			this.showAddModal = true
		},
		closeAddModal() {
			if (this.addSaving) return
			this.showAddModal = false
			this.addError = null
		},
		addProposal() {
			this.addError = null
			const setupDollars = Number(this.newProposal.setup_dollars)
			const annualDollars = Number(this.newProposal.annual_dollars)
			if (!Number.isFinite(setupDollars) || !Number.isFinite(annualDollars)) {
				this.addError = 'Setup and annual costs must be numbers.'
				return
			}
			this.addSaving = true
			const payload = {
				municipality_name_raw: this.newProposal.municipality_name_raw,
				state: this.newProposal.state,
				population: this.newProposal.population || null,
				setup_cents: Math.round(setupDollars * 100),
				annual_cents: Math.round(annualDollars * 100),
				contract_years: this.newProposal.contract_years,
				customer_status: this.newProposal.customer_status,
				outcome: this.newProposal.outcome,
				signed_date: this.newProposal.signed_date || null,
				notes: this.newProposal.notes || null,
			}
			restApi.post('/cwpe/proposals', payload).then(({ data }) => {
				this.showAddModal = false
				this.$router.push(`/cwpe/proposals/${data.id}`)
			}).catch(err => {
				const errMsg = err && err.response && err.response.data && err.response.data.error
				this.addError = typeof errMsg === 'string'
					? errMsg
					: errMsg
						? Object.values(errMsg).flat().join(' · ')
						: 'Save failed.'
			}).finally(() => { this.addSaving = false })
		},
		onBulkImported() {
			// Bulk-parse panel finished a batch → refresh the list + stats so
			// freshly-saved proposals appear immediately.
			this.load()
			this.loadStats()
		},
		outcomeButtonStyle(o) {
			const active = this.newProposal.outcome === o
			if (!active) {
				return { border: '1px solid #cbd5e1', background: 'white', color: '#475569' }
			}
			if (o === 'won') return { border: '1px solid #10b981', background: '#ecfdf5', color: '#064e3b', fontWeight: 500 }
			if (o === 'lost') return { border: '1px solid #f43f5e', background: '#fff1f2', color: '#881337', fontWeight: 500 }
			return { border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontWeight: 500 }
		},
		deleteProposal(p) {
			this.$swal.fire({
				title: 'Delete this proposal?',
				text: `${p.municipality_name_raw}, ${p.state} will be permanently deleted.`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Delete',
				confirmButtonColor: '#dc3545',
			}).then(result => {
				if (!result.isConfirmed) return
				restApi.delete(`/cwpe/proposals/${p.id}`).then(() => {
					this.load()
					this.loadStats()
				}).catch(err => {
					this.$swal.fire({ icon: 'error', title: 'Delete failed', text: err?.response?.data?.error || '' })
				})
			})
		},
		importCsv(e) {
			const file = e.target.files[0]
			if (!file) return
			const fd = new FormData()
			fd.append('file', file)
			restApi.post('/cwpe/proposals/import', fd, { headers: { 'content-type': 'multipart/form-data' } }).then(({ data }) => {
				this.$swal.fire({ icon: 'success', title: `Imported ${data.created || 0} proposals`, text: `${data.skipped || 0} skipped` })
				this.load()
				this.loadStats()
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Import failed', text: err?.response?.data?.error || '' })
			}).finally(() => { if (this.$refs.csvFile) this.$refs.csvFile.value = '' })
		},
		enrichUnmatched() {
			this.enriching = true
			this.enrichSummary = null
			restApi.post('/cwpe/admin/proposals/enrich').then(({ data }) => {
				this.enrichSummary = `Enrichment: ${data.matched}/${data.scanned} matched${data.errors ? ` · ${data.errors} errors` : ''}.`
				this.load()
				this.loadStats()
			}).catch(err => {
				this.enrichSummary = `Enrichment failed: ${err?.response?.data?.message || err?.message || 'unknown'}`
			}).finally(() => { this.enriching = false })
		},
		reparseMissing() {
			this.reparsing = true
			this.reparseSummary = null
			restApi.post('/cwpe/admin/proposals/reparse-missing').then(({ data }) => {
				const skip = data.skipped_no_files || 0
				this.reparseSummary = `Parsed ${data.reparsed}/${data.scanned} proposals${data.errors ? ` · ${data.errors} errors` : ''}${skip ? ` · ${skip} skipped (no files)` : ''}.`
				this.load()
			}).catch(err => {
				this.reparseSummary = `Re-parse failed: ${err?.response?.data?.message || err?.message || 'unknown'}`
			}).finally(() => { this.reparsing = false })
		},
		shortBundleName(name) {
			if (!name) return ''
			return name.replace('Community Foundation', 'CF').replace('Command Center', 'CC')
		},
		fmtSignedCents(c) {
			if (c == null) return '—'
			const sign = c > 0 ? '+' : c < 0 ? '−' : ''
			return sign + '$' + Math.abs(Math.round(c / 100)).toLocaleString('en-US')
		},
		deltaColor(c) {
			if (c == null) return ''
			if (c > 0) return '#0369a1'
			if (c < 0) return '#be123c'
			return '#475569'
		},
		fmtCents(c) {
			if (c == null) return '—'
			return '$' + (c / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
		},
		fmtDate(val) {
			if (!val) return '—'
			return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
		},
		outcomeStyle(o) {
			if (o === 'won') return { background:'#d1fae5', color:'#065f46', borderColor:'#6ee7b7' }
			if (o === 'lost') return { background:'#ffe4e6', color:'#9f1239', borderColor:'#fda4af' }
			if (o === 'pending') return { background:'#fef3c7', color:'#92400e', borderColor:'#fcd34d' }
			return { background:'#f1f5f9', color:'#334155', borderColor:'#cbd5e1' }
		},
	},
}
</script>
